/**
 * V2 Session Helper — clean access to the encrypted PalCare session.
 *
 * The PalCare session (dData) is stored as an AES-encrypted JSON blob
 * in localStorage under the key "PAL". This module provides typed
 * accessor functions so V2 pages don't need to import crypto directly.
 */

import { ReactSession } from '../utils/ReactSession.jsx';
import { AES, enc } from 'crypto-js';

// ---------------------------------------------------------------------------
// Core accessor
// ---------------------------------------------------------------------------

/**
 * Returns the full decrypted session object (dData), or null if not logged in.
 *
 * @returns {object | null}
 */
export function getSession() {
  try {
    const raw = ReactSession.get('PAL');
    if (!raw) return null;
    const decrypted = AES.decrypt(raw, import.meta.env.VITE_ESECRET);
    return JSON.parse(decrypted.toString(enc.Utf8));
  } catch (err) {
    console.error('[session] Failed to decrypt session:', err);
    return null;
  }
}

/**
 * Writes an updated session object back to encrypted storage.
 *
 * @param {object} dData — The full session object to persist
 */
export function setSession(dData) {
  try {
    ReactSession.set('PAL', AES.encrypt(JSON.stringify(dData), import.meta.env.VITE_ESECRET).toString());
  } catch (err) {
    console.error('[session] Failed to encrypt session:', err);
  }
}

// ---------------------------------------------------------------------------
// Convenience accessors
// ---------------------------------------------------------------------------

/** Current campus ID */
export function getCampusId() {
  return getSession()?.acnt ?? null;
}

/** Current user ID */
export function getUserId() {
  return getSession()?.user ?? null;
}

/** Current session ID (used as ClientId in API calls) */
export function getSessionId() {
  return getSession()?.sessionId ?? null;
}

/** Campus-specific configuration for the current campus */
export function getCampusConfig() {
  const s = getSession();
  if (!s?.acnt || !s?.campusData) return null;
  return s.campusData[s.acnt.toString()] ?? null;
}

/** Campus name */
export function getCampusName() {
  return getSession()?.campus ?? '';
}

/**
 * Full campus list for the campus selector dropdown.
 *
 * dData.acntList is an array of plain campus IDs (e.g. [1, 5, 12]).
 * Campus names/config live in dData.campusData[id]. This function
 * transforms the raw IDs into objects the V2 UI expects.
 *
 * @returns {Array<{campus_id: number, campus_name: string, valid: boolean}>}
 */
export function getCampusList() {
  const s = getSession();
  if (!s?.acntList || !s?.campusData) return [];
  return s.acntList.map((id) => ({
    campus_id: id,
    campus_name: s.campusData[id.toString()]?.name || `Campus ${id}`,
    valid: s.campusData[id.toString()]?.valid ?? true,
  }));
}

/**
 * Persist a campus change to the encrypted session.
 * Mirrors V1's setACNT() — updates dData.acnt and copies
 * campus-specific feature flags to the root session object.
 *
 * @param {number} campusId — The new campus ID to persist
 */
export function updateSessionCampus(campusId) {
  const s = getSession();
  if (!s) return;

  const id = campusId.toString();
  const cfg = s.campusData?.[id] || {};

  s.acnt = campusId;
  s.campus = cfg.name || s.campus;
  s.rounds = cfg.is_rounding || 0;
  s.checkin = cfg.is_resident_checkin || 0;
  s.evmSiteId = cfg.location_site_id || '0';
  s.cloud = cfg.is_cloud || 0;
  s.hybrid = cfg.is_hybrid || false;
  s.legacy = cfg.show_legacy || false;
  s.new = cfg.show_new || false;
  s.server_version = cfg.server_version || '';

  setSession(s);
}

/** User role weight (for permission checks) */
export function getRoleWeight() {
  return getSession()?.my_role_weight ?? 10;
}

/** Whether user has at least the given role weight */
export function hasMinRole(minWeight) {
  return getRoleWeight() >= minWeight;
}

/**
 * Converts a campus ID to its GUID for DynamoDB operations.
 * Wraps the V1 `campusToGuid(dData, campusId)` pattern from libs.jsx.
 *
 * @param {number|string} [campusId] — Campus ID to convert, defaults to current campus
 * @returns {string} Campus GUID, or the campus ID as fallback
 */
export function getCampusGuid(campusId) {
  const s = getSession();
  if (!s) return campusId ?? '';
  const id = (campusId ?? s.acnt)?.toString();
  return s.campusData?.[id]?.guid || id;
}

/**
 * Returns the current user's username (for ModifiedBy fields).
 */
export function getUsername() {
  return getSession()?.username ?? '';
}

/**
 * Returns offsite vendor info for the current campus (used in resident/device payloads).
 */
export function getOffsiteInfo() {
  const campus = getCampusConfig();
  return {
    offsite: campus?.offsite_vendor_active ?? false,
    offsite_id: campus?.offsite_vendor_id ?? null,
  };
}
